import { BASE_URL } from "../../config";
import { getApiKey, uuidv4 } from "services/common";
import * as Sentry from "@sentry/react";
import {
  AuthnetHostedTokenResponse,
  AuthnetCompleteRequest,
  AuthnetCompleteResponse,
  AuthnetCheckoutResponse,
  AuthnetCardUpdateResponse,
  AuthnetPayerBody,
} from "./types";

export const AUTHNET_GATEWAY = "FLY";

export const getHostedToken = async (
  sessionToken: string,
  region: string,
  provider: string = AUTHNET_GATEWAY,
): Promise<AuthnetHostedTokenResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api-payments/v1/authnet/hosted-token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": getApiKey(region),
          "X-Session-Token": sessionToken,
          "X-User": "web",
          "X-Request-ID": uuidv4(),
          "X-Payment-Gateway": provider,
        },
      },
    );

    if (!response.ok) {
      Sentry.logger.error("authorizeNetService[hostedForm] error", {
        status: response.status,
      });
      return {
        success: false,
        error: {
          message: `Failed to get hosted token: ${response.statusText}`,
          code: `${response.status}`,
          status: response.status,
        },
      };
    }

    const data = await response.json();
    if (
      !data ||
      typeof data.form_token !== "string" ||
      data.form_token.length === 0
    ) {
      Sentry.logger.error("authorizeNetService[hostedForm] error", {
        code: "MISSING_FORM_REF",
      });
      return {
        success: false,
        error: {
          message: "Hosted form reference missing in response",
          code: "MISSING_FORM_REF",
        },
      };
    }
    Sentry.logger.info("authorizeNetService[hostedForm] ok");
    return { success: true, data: { formToken: data.form_token } };
  } catch (error) {
    Sentry.logger.error("authorizeNetService[hostedForm] error", {
      code: "NETWORK_ERROR",
    });
    return {
      success: false,
      error: {
        message: "Unexpected error requesting hosted token",
        code: "NETWORK_ERROR",
      },
    };
  }
};

export const getHostedTokenByHash = async (
  hash: string,
  ids: number[],
  region: string,
  cardOnly: boolean = false,
  provider: string = AUTHNET_GATEWAY,
): Promise<AuthnetHostedTokenResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api-payments/v1/authnet/hosted-token/${hash}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": getApiKey(region),
          "X-Session-Token": hash,
          "X-User": "web",
          "X-Request-ID": uuidv4(),
          "X-Payment-Gateway": provider,
        },
        body: JSON.stringify({ ids, cardOnly }),
      },
    );

    if (!response.ok) {
      Sentry.logger.error("authorizeNetService[hostedFormByHash] error", {
        status: response.status,
      });
      return {
        success: false,
        error: {
          message: `Failed to get hosted token: ${response.statusText}`,
          code: `${response.status}`,
          status: response.status,
        },
      };
    }

    const data = await response.json();
    if (
      !data ||
      typeof data.form_token !== "string" ||
      data.form_token.length === 0
    ) {
      Sentry.logger.error("authorizeNetService[hostedFormByHash] error", {
        code: "MISSING_FORM_REF",
      });
      return {
        success: false,
        error: {
          message: "Hosted form reference missing in response",
          code: "MISSING_FORM_REF",
        },
      };
    }
    Sentry.logger.info("authorizeNetService[hostedFormByHash] ok");
    return { success: true, data: { formToken: data.form_token } };
  } catch (error) {
    Sentry.logger.error("authorizeNetService[hostedFormByHash] error", {
      code: "NETWORK_ERROR",
    });
    return {
      success: false,
      error: {
        message: "Unexpected error requesting hosted token",
        code: "NETWORK_ERROR",
      },
    };
  }
};

export const checkoutAuthorizeNet = async (
  transId: string,
  ids: number[],
  payer: AuthnetPayerBody,
  region: string,
  hash: string,
  applicationId: string,
  provider: string = AUTHNET_GATEWAY,
): Promise<AuthnetCheckoutResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api-payments/v1/authnet/checkout`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": getApiKey(region),
          "X-User": "web",
          "X-Request-ID": uuidv4(),
          "X-Payment-Gateway": provider,
        },
        body: JSON.stringify({
          hash,
          transId,
          ids,
          payer,
          application_id: applicationId,
        }),
      },
    );

    if (!response.ok) {
      Sentry.logger.error("authorizeNetService[checkoutAuthorizeNet] error", {
        status: response.status,
      });
      return {
        success: false,
        error: {
          message: `Failed to checkout: ${response.statusText}`,
          code: `${response.status}`,
          status: response.status,
        },
      };
    }
    Sentry.logger.info("authorizeNetService[checkoutAuthorizeNet] ok");
    return { success: true, data: undefined };
  } catch (error) {
    Sentry.logger.error("authorizeNetService[checkoutAuthorizeNet] error", {
      code: "NETWORK_ERROR",
    });
    return {
      success: false,
      error: {
        message: "Unexpected error during checkout",
        code: "NETWORK_ERROR",
      },
    };
  }
};

export const updateCardAuthorizeNet = async (
  transId: string,
  hash: string,
  payer: AuthnetPayerBody,
  region: string,
  provider: string = AUTHNET_GATEWAY,
): Promise<AuthnetCardUpdateResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api-payments/v1/authnet/card/${hash}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": getApiKey(region),
          "X-Session-Token": hash,
          "X-User": "web",
          "X-Request-ID": uuidv4(),
          "X-Payment-Gateway": provider,
        },
        body: JSON.stringify({ transId, payer, recurrent: true }),
      },
    );

    if (!response.ok) {
      Sentry.logger.error("authorizeNetService[updateCardAuthorizeNet] error", {
        status: response.status,
      });
      return {
        success: false,
        error: {
          message: `Failed to update card: ${response.statusText}`,
          code: `${response.status}`,
          status: response.status,
        },
      };
    }
    Sentry.logger.info("authorizeNetService[updateCardAuthorizeNet] ok");
    return { success: true, data: undefined };
  } catch (error) {
    Sentry.logger.error("authorizeNetService[updateCardAuthorizeNet] error", {
      code: "NETWORK_ERROR",
    });
    return {
      success: false,
      error: {
        message: "Unexpected error updating card",
        code: "NETWORK_ERROR",
      },
    };
  }
};

export const completeAuthorizeNet = async (
  request: AuthnetCompleteRequest,
  sessionToken: string,
  region: string,
  provider: string = AUTHNET_GATEWAY,
): Promise<AuthnetCompleteResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api-payments/v1/authnet/complete`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": getApiKey(region),
          "X-Session-Token": sessionToken,
          "X-User": "web",
          "X-Request-ID": uuidv4(),
          "X-Payment-Gateway": provider,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      Sentry.logger.error("authorizeNetService[completeAuthorizeNet] error", {
        status: response.status,
        quotation: request.subscription.id_quotation,
      });
      return {
        success: false,
        error: {
          message: `Failed to complete payment: ${response.statusText}`,
          code: `${response.status}`,
          status: response.status,
        },
      };
    }

    const data = await response.json();
    if (!data || typeof data.hash !== "string" || data.hash.length === 0) {
      Sentry.logger.error("authorizeNetService[completeAuthorizeNet] error", {
        code: "MISSING_HASH",
        quotation: request.subscription.id_quotation,
      });
      return {
        success: false,
        error: {
          message: "Subscription hash missing in response",
          code: "MISSING_HASH",
        },
      };
    }
    Sentry.logger.info("authorizeNetService[completeAuthorizeNet] ok", {
      quotation: request.subscription.id_quotation,
    });
    return { success: true, data: { hash: data.hash } };
  } catch (error) {
    Sentry.logger.error("authorizeNetService[completeAuthorizeNet] error", {
      code: "NETWORK_ERROR",
    });
    return {
      success: false,
      error: {
        message: "Unexpected error completing payment",
        code: "NETWORK_ERROR",
      },
    };
  }
};
